import PublicLayout from "@/components/layout/PublicLayout";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Eye, Users, Heart, Ban, BarChart3 } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

export default function About() {
  const { settings } = useSettings();
  const brand = settings?.brand?.name || "NewStarAds";
  const values = [
    { icon: ShieldCheck, t: "Compliance first", d: "Every campaign is designed to follow YouTube's policies. We protect your channel, not endanger it." },
    { icon: Eye, t: "Radical transparency", d: "We report only real, connected data. If a number can't be verified, we won't show it." },
    { icon: Ban, t: "No bots, ever", d: "We do not sell fake views, subscribers or artificial traffic. It doesn't work and it harms creators." },
    { icon: Heart, t: "Creator-focused", d: "We care about sustainable growth and genuine audiences — not vanity metrics." },
  ];
  return (
    <PublicLayout>
      <SEO title={`About ${brand} | YouTube Marketing Agency`} description={`${brand} is a professional YouTube marketing agency helping creators and businesses grow visibility through transparent, policy-compliant promotion.`} />
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-8">
        <Reveal>
          <p className="text-xs uppercase tracking-widest font-semibold text-primary">About us</p>
          <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">Honest YouTube marketing, done professionally</h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">{brand} is a YouTube marketing agency built on a simple belief: creators and businesses deserve promotion that is transparent, compliant and genuinely effective. We help you reach real, relevant viewers using YouTube's own advertising and discovery surfaces — never bots, fake engagement or misleading guarantees.</p>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">Results in YouTube marketing always vary based on content, niche and budget. We won't promise you viral fame or a guaranteed number of views. What we will do is bring strategy, precise targeting and honest reporting to every campaign.</p>
        </Reveal>
      </section>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14 grid md:grid-cols-2 gap-6">
        {values.map((v, i) => (
          <Reveal key={i} delay={(i % 2) * 0.05} className="rounded-2xl border border-slate-200 p-7">
            <div className="w-11 h-11 grid place-items-center rounded-xl bg-primary/10 text-primary mb-4"><v.icon className="w-6 h-6" strokeWidth={1.5} /></div>
            <h3 className="font-display text-xl font-bold">{v.t}</h3>
            <p className="mt-2 text-slate-600 leading-relaxed">{v.d}</p>
          </Reveal>
        ))}
      </section>
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-24 text-center">
        <Reveal className="rounded-2xl bg-slate-950 text-white p-12">
          <h2 className="font-display text-3xl font-bold tracking-tight">Ready to grow the right way?</h2>
          <p className="mt-3 text-slate-300">Request a free consultation and let's talk honestly about your goals.</p>
          <Link to="/consultation"><Button size="lg" className="mt-6 rounded-full px-8">Get a Free Consultation</Button></Link>
        </Reveal>
      </section>
    </PublicLayout>
  );
}
